import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '../service/layout.service';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteDropdownClickEvent } from 'primeng/autocomplete';
import { FormsModule } from '@angular/forms';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { AuthService, EbpService, ProductService, SearchService, ToastService } from '@app/core/services';
import { TooltipModule } from 'primeng/tooltip';
import { Product } from '@app/core/interfaces';
import { TagModule } from 'primeng/tag';
import { environment } from '@env/environment';

@Component({
    selector: 'app-topbar',
    standalone: true,
    imports: [RouterModule, CommonModule, StyleClassModule, AutoComplete, FormsModule, OverlayBadgeModule, TooltipModule, TagModule],
    template: ` <div class="layout-topbar">
        <div class="layout-topbar-logo-container">
            <button class="layout-menu-button layout-topbar-action inline-flex xl:!hidden" (click)="layoutService.onMenuToggle()">
                <i class="pi pi-bars"></i>
            </button>
            <a class="layout-topbar-logo" routerLink="/">
                <img src="logo.png" alt="REI Logo" width="40" />
                <span>REI Item</span>
            </a>
        </div>

        <div class="layout-topbar-actions">
            <p-autocomplete [(ngModel)]="value" [suggestions]="items" (onKeyUp)="onKeyUpEvent($event)" (onDropdownClick)="onDropdownClickEvent($event)" (completeMethod)="search($event)" (onClear)="onClearEvent()" (onSelect)="onClearEvent()" placeholder="Recherche" fluid [showClear]="true" [minLength]="3" [dropdown]="true" [lazy]="true" size="large" dropdownIcon="pi pi-search" class="min-w-80">
                <ng-template let-product #item>
                    <div class="flex items-center gap-2 w-full" [routerLink]="['/products', product.id]">
                        <img [src]="apiUrl + product.photo_couverture" class="object-cover aspect-square" width="40" height="40" />
                        <div class="flex flex-row gap-2 grow">
                            <div class="flex flex-1 flex-col gap-2">
                                <span class="text-primary-500 dark:text-primary-400">{{product.marque?.nom}}</span>
                                <span class="">{{product.reference}}</span>
                            </div>
                            <div class="flex items-end flex-col gap-2">
                                <p-tag severity="contrast" [value]="'#' + product.id_old" class="!text-base" />
                            </div>
                        </div>
                    </div>
                </ng-template>
                <ng-template #header>
                    <div class="text-center p-2 border-b border-b-surface-200 dark:border-b-surface-700">{{totalRecords}} Produits au total</div>
                </ng-template>
                <!-- <ng-template #footer>
                    <div class="text-center p-2">
                    </div>
                </ng-template> -->
            </p-autocomplete>
        </div>

        <div class="layout-topbar-actions">
            <div class="layout-config-menu">
                <button type="button" class="layout-topbar-action" (click)="onFetchExcel()" [disabled]="isFetchingExcel" pTooltip="Export stocks liste" tooltipPosition="bottom">
                    <i class="pi pi-file-excel" [ngClass]="{'pi-spin': isFetchingExcel}"></i>
                </button>
            </div>
            <div class="layout-config-menu">
                <button type="button" class="layout-topbar-action" (click)="onFetchEbp()" [disabled]="isFetchingEbp" pTooltip="Mise à jour EBP" tooltipPosition="bottom">
                    <i class="pi pi-sync" [ngClass]="{'pi-spin': isFetchingEbp}"></i>
                </button>
            </div>
            <div class="layout-config-menu">
                <button type="button" class="layout-topbar-action"  pTooltip="Mes notifications" tooltipPosition="bottom">
                    <p-overlaybadge severity="danger">
                        <i class="pi pi-bell"></i>
                    </p-overlaybadge>
                </button>
            </div>
            <div class="layout-config-menu">
                <button type="button" class="layout-topbar-action" (click)="toggleDarkMode()" pTooltip="Luminosité du thème" tooltipPosition="bottom">
                    <i [ngClass]="{ 'pi ': true, 'pi-moon': layoutService.isDarkTheme(), 'pi-sun': !layoutService.isDarkTheme() }"></i>
                </button>
            </div>

            <button class="layout-topbar-menu-button layout-topbar-action" pStyleClass="@next" enterFromClass="hidden" enterActiveClass="animate-scalein" leaveToClass="hidden" leaveActiveClass="animate-fadeout" [hideOnOutsideClick]="true">
                <i class="pi pi-ellipsis-v"></i>
            </button>

            <div class="layout-topbar-menu hidden xl:block">
                <div class="layout-topbar-menu-content">
                    <button type="button" class="layout-topbar-action"  pTooltip="Mon profil" tooltipPosition="bottom">
                        <i class="pi pi-user"></i>
                        <span>Profil</span>
                    </button>
                    <button type="button" (click)="authService.logout()" class="layout-topbar-action"  pTooltip="Déconnexion" tooltipPosition="bottom">
                        <i class="pi pi-sign-out"></i>
                        <span>Déconnexion</span>
                    </button>
                </div>
            </div>
        </div>
    </div>`
})
export class AppTopbar {
    readonly apiUrl: string = environment.apiUrl;
    ebpService: EbpService = inject(EbpService);
    searchService: SearchService = inject(SearchService);
    toastService: ToastService = inject(ToastService);
    productService: ProductService = inject(ProductService);
    router: Router = inject(Router);

    items: Product[] = [];
    totalRecords: number = 0;
    value: any;

    isFetchingEbp: boolean = false
    isFetchingExcel: boolean = false

    constructor(
        private activatedRoute: ActivatedRoute,
        public layoutService: LayoutService,
        public authService: AuthService
    ) {
        this.activatedRoute.queryParams.subscribe(params => {
            if (params['search']) {
                this.value = params['search'];
                this.search({ query: this.value } as AutoCompleteCompleteEvent);
            }
        });
        this.searchService.search$.subscribe(search => {
            if (search !== this.value) {
                this.value = search;
                this.search({ query: this.value } as AutoCompleteCompleteEvent);
            }
        });
    }

    toggleDarkMode() {
        this.layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
    }

    search(event: AutoCompleteCompleteEvent) {
        this.productService.getCollection({ recherche_globale: event.query, size: 3, fields: {produits: 'id,id_old,reference,marque,photo_couverture'} }).subscribe({
            next: (data) => {
                this.items = data.results
                this.totalRecords = data.total
            },
            error: () => {
                this.items = []
                this.totalRecords = 0
            },
        });
    }

    onKeyUpEvent(event: KeyboardEvent) {
        if(event.key === 'Enter' && this.items.length > 0) {
            if (this.items.length > 1) {
                this.searchService.setSearchTerm(this.value);
                // redirect to search page
                this.router.navigate(['/products'], { queryParams: { search: this.value } });
            } else {
                const firstItem = this.items[0];
                // redirect to product detail page
                this.router.navigate(['/products', firstItem.id]);
            }
        }
    }

    onDropdownClickEvent(event: AutoCompleteDropdownClickEvent) {
        this.searchService.setSearchTerm(this.value);
        // redirect to search page
        this.router.navigate(['/products'], { queryParams: { search: this.value } });
    }

    onClearEvent() {
        this.items = [];
        this.totalRecords = 0;
        this.searchService.clearSearchTerm();
    }

    onFetchEbp() {
        this.isFetchingEbp = true
        this.ebpService
            .getRefresh()
            .subscribe({
                complete: () => {
                    this.isFetchingEbp = false
                    this.toastService.add({ severity: 'success', summary: 'Succès', detail: 'Récupération des erreurs EBP effectuées.' })
                },
                error: () => this.isFetchingEbp = false,
            })
    }

    onFetchExcel() {
        this.isFetchingExcel = true
        this.productService
            .getExportExcel()
            .subscribe({
                next: (response) => {
                    const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'stocks.xlsx';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                },
                complete: () => this.isFetchingExcel = false,
                error: () => this.isFetchingExcel = false,
            })
    }
}
